import React from 'react';
import { Text, TextAs, TextFontFamilies, TextSizes } from '@clockout-dev/design-elements';
import UserHeaderMeta from './UserHeaderMeta';
import UserHeaderActions from './UserHeaderActions';
import { USER_HEADER_COPY } from '../user.constants';
import styles from './styles/user-header.module.scss';

/**
 * Page header for the User page (Figma Admin 3.0 «UserHeader», 296:22210 — KAN-738).
 *
 *   John Doe                                          [More actions ▾] [👁 View as user]
 *   Riverside Logistics | Salaried · Twice a month | Opted in and enrolled Apr 20, 2026 | ID 848359935 ⧉
 *
 * Presentational: every prop is produced by `UserHeaderController`
 * (`buildHeaderModel`, mixed into UserController) as final text and data, and
 * every action is the page's existing handler — the header decides no rule,
 * and only triggers the SSO, Reconcile and Restrict flows, never owns them.
 *
 * The breadcrumb ([user] › Reports › John Doe) lives in the app header: the
 * page publishes it through `UserHeaderController.publishBreadcrumb`.
 *
 * The header degrades piece by piece: without enrollment dates, an employer
 * or a pay type, that segment is simply absent and the rest still renders.
 *
 * @param {Object} props
 * @param {string} props.name
 * @param {string | null} props.userId The member's `client_id` — shown
 *   and copied by the ID chip, the same id More actions › Copy user ID copies.
 * @param {string | null} [props.primaryEmployer] `report.employer` from sync-status.
 * @param {string | null} [props.payTypeLine] "Salaried · Every 2 weeks", or null.
 * @param {Array<Object>} [props.payrolls] Popover rows, as DropdownMenu items
 *   (`UserHeaderController.toPayrollSourceMenuItems`).
 * @param {boolean} [props.payrollSourcesOpen] Popover open state (page state).
 * @param {(next: boolean, info?: Object) => void} [props.onPayrollSourcesOpenChange]
 * @param {number} [props.sourceCount] Drives "+N".
 * @param {string | null} [props.sourcesLabel] "2 payroll sources".
 * @param {string} [props.sourcesTitle] "2 Payroll sources" (popover caption).
 * @param {{ variant: string, lead: string | null, warning: string | null }} [props.enrollmentChip]
 * @param {Array<Object>} [props.moreActions] More actions as DropdownMenu items,
 *   built by `UserHeaderController` (copy, gating, icons and handlers).
 * @param {boolean} [props.canOpenSso]
 * @param {() => void} props.onOpenSso
 * @param {(id: string) => void} [props.onUserIdCopied] Inline copy succeeded.
 * @param {(error: Error) => void} [props.onUserIdCopyFailed]
 */
function UserHeader({
    name,
    userId,
    primaryEmployer = null,
    payTypeLine = null,
    payrolls = [],
    sourceCount = 0,
    sourcesLabel = null,
    sourcesTitle = '',
    enrollmentChip = null,
    payrollSourcesOpen = false,
    onPayrollSourcesOpenChange,
    moreActions = [],
    canOpenSso = false,
    onOpenSso,
    onUserIdCopied,
    onUserIdCopyFailed,
}) {
    const safeName = name || USER_HEADER_COPY.UNKNOWN_NAME;

    return (
        <header className={styles.header}>
            <div className={styles.content}>
                <div className={styles.identity}>
                    <Text
                        as={TextAs.H1}
                        size={TextSizes.DISPLAY_MEDIUM}
                        fontFamily={TextFontFamilies.DISPLAY}
                        className={styles.name}
                        title={safeName}
                    >
                        {safeName}
                    </Text>
                    <UserHeaderMeta
                        userId={userId}
                        primaryEmployer={primaryEmployer}
                        payTypeLine={payTypeLine}
                        payrolls={payrolls}
                        sourceCount={sourceCount}
                        sourcesLabel={sourcesLabel}
                        sourcesTitle={sourcesTitle}
                        enrollmentChip={enrollmentChip}
                        payrollSourcesOpen={payrollSourcesOpen}
                        onPayrollSourcesOpenChange={onPayrollSourcesOpenChange}
                        onCopyUserId={onUserIdCopied}
                        onCopyUserIdError={onUserIdCopyFailed}
                    />
                </div>

                <UserHeaderActions moreActions={moreActions} canOpenSso={canOpenSso} onOpenSso={onOpenSso} />
            </div>
        </header>
    );
}

export default UserHeader;
